/**
 * Batch Service Layer
 *
 * API 호출 및 Domain 모델 변환을 담당
 * Layered Architecture 패턴 적용
 */

import type { BatchListApiResponse, BatchDetailApiResponse, BatchApiDto } from '@/types/api'

import { getData } from '@/utils'

// ============================================
// Domain Types
// ============================================

/**
 * Batch 도메인 모델
 */
export interface Batch {
  /** Batch 고유 ID */
  id: string

  /** Batch 이름 */
  name: string
}

// ============================================
// Mappers
// ============================================

/**
 * API Batch DTO → Domain Batch
 */
function toDomainBatch(apiBatch: BatchApiDto): Batch {
  return {
    id: apiBatch.id,
    name: apiBatch.name,
  }
}

/**
 * API Batch 배열 → Domain Batch 배열
 */
function toDomainBatches(apiBatches: BatchApiDto[]): Batch[] {
  return apiBatches.map(toDomainBatch)
}

// ============================================
// Batch Queries (조회)
// ============================================

/**
 * Batch 목록 조회
 *
 * @returns Domain Batch 배열
 */
export async function getBatches(): Promise<Batch[]> {
  try {
    const apiResponse = await getData<BatchListApiResponse>('/batch')

    // API 응답이 배열인지 확인
    const batchList = Array.isArray(apiResponse)
      ? apiResponse
      : ((apiResponse as { list?: BatchApiDto[] }).list ?? [])

    return toDomainBatches(batchList)
  } catch (error) {
    console.error('Failed to fetch batches:', error)
    throw error
  }
}

/**
 * Batch 상세 조회
 *
 * @param batchId - Batch ID
 * @returns Domain Batch
 */
export async function getBatchById(batchId: string): Promise<Batch> {
  try {
    const apiResponse = await getData<BatchDetailApiResponse>(`/batch/${batchId}`)

    return toDomainBatch(apiResponse as BatchApiDto)
  } catch (error) {
    console.error('Failed to fetch batch by ID:', error)
    throw error
  }
}

// ============================================
// Legacy Compatibility (기존 API 호환)
// ============================================

/**
 * @deprecated Use getBatches() instead
 *
 * 기존 callback 스타일 API 호환용
 */
export function getBatchList(setState: (batches: Batch[]) => void): void {
  getBatches()
    .then((batches) => setState(batches))
    .catch((error) => console.error('getBatchList error:', error))
}
